<template>
  <!-- A small drawing of a page in one of the templates. Not the resume itself: just enough to
       tell the looks apart at a glance. -->
  <div class="aspect-[210/297] w-full rounded-md bg-white shadow-sm ring-1 ring-black/5 overflow-hidden relative" :style="sidebarStyle" aria-hidden="true">
    <!-- single column family -->
    <div v-if="['modern', 'classic', 'minimal', 'harvard', 'compact', 'executive', 'timeline'].includes(template)" class="p-[9%] flex flex-col gap-[4%] h-full">
      <template v-if="template === 'modern'">
        <div class="h-[14%] rounded-sm" :style="{ backgroundColor: accent }"></div>
        <div class="h-[3%] w-2/5 rounded-full" :style="{ backgroundColor: accent }"></div>
        <div :class="LINE"></div><div :class="LINE + ' w-11/12'"></div>
        <div class="h-[3%] w-1/3 rounded-full mt-[2%]" :style="{ backgroundColor: accent }"></div>
        <div :class="LINE"></div><div :class="LINE + ' w-4/5'"></div><div :class="LINE"></div>
      </template>
      <template v-else-if="template === 'classic' || template === 'harvard'">
        <div class="h-[4%] w-1/2 mx-auto rounded-full bg-gray-700"></div>
        <div class="h-[2.5%] w-2/3 mx-auto rounded-full bg-gray-300"></div>
        <div :class="['h-px w-full my-[2%]', template === 'harvard' ? 'bg-gray-900' : 'bg-gray-400']"></div>
        <div class="h-[3%] w-1/4 rounded-full bg-gray-600"></div>
        <div :class="LINE"></div><div :class="LINE + ' w-11/12'"></div>
        <div :class="['h-px w-full my-[2%]', template === 'harvard' ? 'bg-gray-900' : 'bg-gray-400']"></div>
        <div class="h-[3%] w-1/3 rounded-full bg-gray-600"></div><div :class="LINE"></div>
      </template>
      <template v-else-if="template === 'minimal'">
        <div class="h-[4%] w-2/5 rounded-full bg-gray-800"></div>
        <div class="h-[2.5%] w-3/5 rounded-full bg-gray-300 mb-[3%]"></div>
        <div :class="LINE"></div><div :class="LINE"></div><div :class="LINE + ' w-4/5 mb-[3%]'"></div>
        <div :class="LINE"></div><div :class="LINE + ' w-11/12'"></div>
      </template>
      <template v-else-if="template === 'compact'">
        <div class="flex items-center gap-[4%]"><div class="h-[4%] w-2/5 rounded-full bg-gray-800 flex-1"></div><div class="w-[16%] aspect-square rounded-full bg-gray-300"></div></div>
        <div class="h-px w-full bg-gray-300"></div>
        <div class="h-[2.5%] w-1/4 rounded-full" :style="{ backgroundColor: accent }"></div>
        <div :class="LINE"></div><div :class="LINE"></div><div :class="LINE + ' w-4/5'"></div>
        <div class="h-[2.5%] w-1/4 rounded-full mt-[1%]" :style="{ backgroundColor: accent }"></div>
        <div :class="LINE"></div><div :class="LINE"></div><div :class="LINE + ' w-3/5'"></div>
      </template>
      <template v-else-if="template === 'executive'">
        <div class="h-[18%] rounded-sm flex items-center justify-between px-[6%]" :style="{ backgroundColor: accent }"><div class="h-[26%] w-1/2 rounded-full bg-white/80"></div><div class="h-[60%] aspect-square rounded-full bg-white/40"></div></div>
        <div class="flex items-center gap-[4%]"><div class="h-[130%] w-[3%] self-stretch" :style="{ backgroundColor: accent }"></div><div class="h-[3%] w-1/3 rounded-full bg-gray-700"></div></div>
        <div :class="LINE"></div><div :class="LINE + ' w-11/12'"></div>
        <div class="flex items-center gap-[4%]"><div class="w-[3%] self-stretch" :style="{ backgroundColor: accent }"></div><div class="h-[3%] w-1/4 rounded-full bg-gray-700"></div></div>
        <div :class="LINE"></div><div :class="LINE + ' w-4/5'"></div>
      </template>
      <template v-else>
        <div class="flex items-center gap-[4%]"><div class="h-[4%] w-2/5 rounded-full flex-1" :style="{ backgroundColor: accent }"></div><div class="w-[16%] aspect-square rounded-full bg-gray-300"></div></div>
        <div class="h-[3%] w-1/4 rounded-full bg-gray-600"></div>
        <div v-for="n in 3" :key="n" class="relative pl-[8%] border-l-2" :style="{ borderColor: accent + '66' }">
          <div class="absolute -left-[3px] top-0 w-[5px] h-[5px] rounded-full" :style="{ backgroundColor: accent }"></div>
          <div :class="LINE + ' w-2/3 mb-[3%]'"></div><div :class="LINE"></div>
        </div>
      </template>
    </div>

    <!-- label column family -->
    <div v-else-if="template === 'europass' || template === 'lebenslauf'" class="p-[9%] flex flex-col gap-[4%] h-full">
      <div class="flex items-start gap-[5%] pb-[3%] border-b-2" :style="{ borderColor: template === 'europass' ? accent : '#374151' }">
        <div v-if="template === 'europass'" class="w-[22%] aspect-square rounded-sm bg-gray-300"></div>
        <div class="flex-1 space-y-[6%]"><div class="h-[5px] w-3/5 rounded-full" :style="{ backgroundColor: template === 'europass' ? accent : '#1f2937' }"></div><div :class="LINE + ' w-4/5'"></div></div>
        <div v-if="template === 'lebenslauf'" class="w-[20%] aspect-[3.5/4.5] rounded-sm bg-gray-300"></div>
      </div>
      <div :class="template === 'europass' ? 'h-[4%] rounded-sm' : 'h-[3%] border-b border-gray-300'" :style="template === 'europass' ? { backgroundColor: accent + '26' } : null"></div>
      <div v-for="n in 3" :key="n" class="flex gap-[6%]"><div class="w-[24%] h-[3px] rounded-full bg-gray-400 mt-[2px]"></div><div class="flex-1 space-y-[5%]"><div :class="LINE + ' w-3/4'"></div><div :class="LINE"></div></div></div>
    </div>

    <!-- sidebar family: the coloured column is the page's own background -->
    <div v-else class="h-full flex" :class="template === 'sidebar' ? '' : 'flex-row-reverse'">
      <div class="w-[32%] p-[5%] flex flex-col items-center gap-[5%]">
        <div class="w-[70%] aspect-square rounded-full" :class="template === 'sidebar' ? 'bg-white/50' : 'bg-gray-300'"></div>
        <div v-for="n in 4" :key="n" class="h-[2.5%] w-full rounded-full" :class="template === 'sidebar' ? 'bg-white/45' : 'bg-gray-300'"></div>
      </div>
      <div class="flex-1 p-[6%] flex flex-col gap-[4%]">
        <div class="h-[4%] w-3/5 rounded-full" :style="{ backgroundColor: accent }"></div>
        <div :class="LINE + ' w-2/5'"></div>
        <div class="h-[2.5%] w-1/3 rounded-full mt-[2%] bg-gray-600"></div>
        <div :class="LINE"></div><div :class="LINE + ' w-11/12'"></div><div :class="LINE + ' w-4/5'"></div>
        <div class="h-[2.5%] w-1/3 rounded-full mt-[2%] bg-gray-600"></div>
        <div :class="LINE"></div><div :class="LINE + ' w-3/4'"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  template: { type: String, default: 'modern' },
  accent: { type: String, default: '#2563eb' },
})

const LINE = 'h-[2.5%] w-full rounded-full bg-gray-200'

// The sidebar templates colour a third of the page, as the real ones do.
const sidebarStyle = computed(() => {
  if (props.template === 'sidebar') return { backgroundImage: `linear-gradient(to right, ${props.accent} 0 32%, transparent 32%)` }
  if (props.template === 'twocolumn') return { backgroundImage: 'linear-gradient(to left, #f3f4f6 0 32%, transparent 32%)' }
  return null
})
</script>
